import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/useSubscription';

interface CancellationBannerProps {
  periodEndDate: string | null;
}

/**
 * Banner displayed when a subscription is scheduled to cancel at period end.
 * Shows the end date and provides option to reactivate via in-app API.
 */
export function CancellationBanner({ periodEndDate }: CancellationBannerProps) {
  const { cancelScheduledCancellation, isCancelScheduledCancellationLoading } = useSubscription();

  // Format the end date for display
  const formattedDate = periodEndDate
    ? new Date(periodEndDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'the end of your billing period';

  const handleReactivate = async () => {
    try {
      await cancelScheduledCancellation();
    } catch {
      // Error handling is done in the hook via toast
    }
  };

  return (
    <div className="rounded-lg border border-warning-200 bg-warning-100/10 px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 shrink-0 text-warning-200" />
          <div>
            <p className="font-medium text-warning-200">
              Your subscription is scheduled to end
            </p>
            <p className="text-sm text-muted-foreground">
              You'll retain access until {formattedDate}. After that, you'll need to
              resubscribe to continue using Sapling.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleReactivate}
          loading={isCancelScheduledCancellationLoading}
          className="shrink-0"
        >
          Reactivate
        </Button>
      </div>
    </div>
  );
}
